import { GenericBrandProvider } from "./brand-context";
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import * as chameleonjs from "chameleonjs";
import deepmerge from "deepmerge";
import { isLocalDev } from "../../utils/env";
import { Redirect } from "react-router-dom";
import { ERROR_SYSTEM } from "../../../main/route";
import {
  combineStatusesWithResponseDetail,
  LoadStatusWithResponseDetail,
} from "../../utils/async-status";
import { getAppBasePath } from "../../../main/utils";
import { noticeError } from "../../../services/monitoring";

// Is there a better way to do this?
type BrandData = any;

function useChameleon(product: string) {
  const [brand, setBrand] = useState<BrandData | null>(null);
  const [status, setStatus] = useState(LoadStatusWithResponseDetail.PENDING);

  useEffect(() => {
    setBrand(null);
    const brandId =
      new URLSearchParams(window.location.search).get("brand") ||
      window.location.hostname;
    chameleonjs
      .loadBrand(brandId, product)
      .then((info) => {
        setBrand({ id: brandId, ...info });
        setStatus(LoadStatusWithResponseDetail.OK);
      })
      .catch((e) => {
        noticeError(e);
        setStatus(LoadStatusWithResponseDetail.ERROR);
      });
  }, [product]);

  return useMemo(() => ({ brand, status }), [brand, status]);
}

function useLocalBrand() {
  const [localBrand, setLocalBrand] = useState<BrandData | null>(null);
  const [localStatus, setStatus] = useState(
    LoadStatusWithResponseDetail.PENDING
  );

  const setBrand = useCallback((data) => {
    setLocalBrand(data);
    setStatus(LoadStatusWithResponseDetail.OK);
  }, []);

  useEffect(() => {
    const { REACT_APP_LOCAL_BRAND } = process.env;

    if (isLocalDev()) {
      fetch(`${getAppBasePath()}/local-brands/${REACT_APP_LOCAL_BRAND}.json`)
        .then((resp) => resp.json())
        .then((json) => setBrand(json.data ?? json))
        .catch(() => {
          setBrand({});
        });
    } else {
      setBrand({});
    }
  }, [setBrand]);

  return useMemo(() => ({ localBrand, localStatus }), [
    localBrand,
    localStatus,
  ]);
}

interface ChameleonBrandProviderProps {
  product: string;
  children?: ReactNode;
  render?: (brand: any) => ReactNode;
}

export default function ChameleonBrandProvider({
  children,
  product,
  render,
}: ChameleonBrandProviderProps) {
  const { brand, status } = useChameleon(product);
  const { localBrand, localStatus } = useLocalBrand();

  const finalStatus = combineStatusesWithResponseDetail([status, localStatus]);

  const finalBrand = useMemo(() => {
    if (brand && localBrand) {
      return deepmerge(brand, localBrand);
    }
    return null;
  }, [brand, localBrand]);

  if (finalStatus === LoadStatusWithResponseDetail.ERROR) {
    return <Redirect to={ERROR_SYSTEM} />;
  }

  if (finalStatus === LoadStatusWithResponseDetail.PENDING) {
    return null;
  }

  return (
    <GenericBrandProvider brand={finalBrand}>
      {render ? render(finalBrand) : children}
    </GenericBrandProvider>
  );
}
